import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { useQuerySourceSuggestionsByKeyword } from '#pplx/frontend/apps/ask/ask-input/ask-input-plus-menu/hooks/useQuerySourceSuggestionsByKeyword.js';
import { useQuerySourceSuggestionsBySourceLabel } from '#pplx/frontend/apps/ask/ask-input/ask-input-plus-menu/hooks/useQuerySourceSuggestionsBySourceLabel.js';
import { useUrlSourceSuggestions } from '#pplx/frontend/apps/ask/ask-input/ask-input-plus-menu/hooks/useUrlSourceSuggestions.js';
import { getQueryWithoutSourceMentions } from '#pplx/frontend/apps/ask/ask-input/query-serialization/lexicalNodeToQuery.js';
import { useConnectorStatus } from '#pplx/frontend/apps/ask/connectors/hooks/useConnectorStatus.js';
import { useOpenConnectorModalBySourceId } from '#pplx/frontend/apps/ask/connectors/modal-navigation/useOpenConnectorModalBySourceId.js';
import { getConnectorAvatar } from '#pplx/frontend/apps/ask/files/file-utils/fileUtils.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useGlobalAvailableSources } from '#pplx/frontend/apps/ask/hooks/shared-source-selection-hooks/useSharedSourceSelection.js';
import { useSkillModal } from '#pplx/frontend/apps/ask/skills/hooks/useSkillModal.js';
import { useSources } from '#pplx/frontend/apps/ask/sources/hooks/useSources.js';
import { useUserInput, useUserInputJson } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useCallback, useMemo } from 'react';
const ConnectorsEnableModal = Dynamic(async () => {
    const { ConnectorsEnableModal } = await import('#pplx/frontend/apps/ask/connectors/modals/ConnectorsEnableModal.js');
    return { default: ConnectorsEnableModal };
});
const REASON = 'source-suggestion-connect';
export const useAskInputSourceSuggestions = ({ enabled = true, maxSuggestions, onSourceMentionSelect, productSurface, }) => {
    const { sources, addSource } = useGlobalAvailableSources();
    const { getSourceLabel } = useSources({ enabled, productSurface });
    const { openModal } = useModalContext();
    const openConnectorModal = useOpenConnectorModalBySourceId({
        reason: 'skill-modal-connector',
        openModal,
    });
    const openSkillModal = useSkillModal({ openModal, openConnectorModal });
    const { getConnectionType } = useConnectorStatus({ reason: REASON });
    const openPaywallUpsell = usePaywallUpsell();
    const rawQuery = useUserInput();
    const userInputJson = useUserInputJson();
    const query = useMemo(() => getQueryWithoutSourceMentions(userInputJson?.root, rawQuery), [rawQuery, userInputJson]);
    const { suggestions: urlSuggestions } = useUrlSourceSuggestions({
        enabled,
        omittedSources: sources,
        productSurface,
    });
    const { suggestions: labelSuggestions } = useQuerySourceSuggestionsBySourceLabel({
        enabled,
        query,
        maxSuggestions,
        omittedSources: sources,
        productSurface,
    });
    const { suggestions: keywordSuggestions } = useQuerySourceSuggestionsByKeyword({
        enabled,
        query,
        maxSuggestions,
        omittedSources: sources,
        productSurface,
    });
    // URL suggestions (from qsources) take priority, then label matches, then keyword matches.
    // Deduped and sliced to maxSuggestions.
    const suggestions = useMemo(() => {
        if (!enabled) {
            return [];
        }
        const combined = new Map();
        for (const item of [...urlSuggestions, ...labelSuggestions, ...keywordSuggestions]) {
            if (maxSuggestions !== undefined && combined.size >= maxSuggestions) {
                break;
            }
            if (!combined.has(item.source)) {
                combined.set(item.source, item);
            }
        }
        return Array.from(combined.values());
    }, [enabled, urlSuggestions, labelSuggestions, keywordSuggestions, maxSuggestions]);
    const handleOpenPaywall = useCallback(() => {
        openPaywallUpsell({ origin: ModalOrigin.SOURCES_VIEW_MORE });
    }, [openPaywallUpsell]);
    const onConnectSource = useCallback((source) => {
        const connectorId = source;
        openModal(ConnectorsEnableModal, {
            connectorName: connectorId,
            connectorAvatar: getConnectorAvatar(connectorId),
            connectionType: getConnectionType(connectorId),
            referrer: 'ask',
            openInNewTab: true,
            reason: REASON,
            handleOpenPaywall,
            openSkillModal,
        }, { replace: true });
    }, [openModal, getConnectionType, handleOpenPaywall, openSkillModal]);
    const onSuggestSource = useCallback((source) => {
        if (source !== 'web') {
            onSourceMentionSelect?.(source, getSourceLabel(source));
        }
        addSource(source);
    }, [addSource, getSourceLabel, onSourceMentionSelect]);
    return useMemo(() => {
        if (!enabled) {
            return {
                showSourceSuggestions: false,
            };
        }
        return {
            showSourceSuggestions: true,
            query,
            suggestions,
            onSuggestSource,
            onConnectSource,
        };
    }, [query, enabled, suggestions, onSuggestSource, onConnectSource]);
};
//# sourceMappingURL=useAskInputSourceSuggestions.js.map